import { useContainerWidth } from "@/hooks/useContainerWidth";
import { SaveIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageTitle } from "../shared/PageTitle";
import { SplitContentEditor } from "./splitContent/SplitContentEditor";
import { Button } from "@/components/ui/button";
import { getContentDetails, getScreenSizeDetails, updateContentSizes } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SplitContentNode } from "./splitContent/splitContentTypes";
import { useEffect, useMemo, useState } from "react";
import { showApiError, showSuccess } from "@/lib/toast";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useSearchParams } from "react-router";
import { ROUTES } from "@/constants/routeConstants";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";

export const SplitContentDetailPage = () => {
  const { t } = useTranslation();
  const [queryParams] = useSearchParams();
  const contentGuid = queryParams.get("Id");
  const activeFirm = useSelector(selectActiveFirm);

  const [updatedLayout, setUpdatedLayout] = useState<SplitContentNode>();

  useBreadcrumbs([{ name: t("entities.content"), path: ROUTES.CONTENT }]);

  const queryClient = useQueryClient();
  const updateSplitContentMutation = useMutation({
    mutationFn: () => {
      return updateContentSizes({
        firmGuid: String(activeFirm?.firmGuid),
        contentGuid: contentGuid ?? "",
        screenSizeId: contentDetails?.screenSizeintId ?? 0,
        layoutConfig: JSON.stringify(updatedLayout ?? {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentGroupDetails"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const { data: contentDetailsData, isLoading } = useQuery({
    queryKey: ["contentDetails", contentGuid],
    queryFn: () => getContentDetails(String(activeFirm?.firmGuid), contentGuid ?? ""),
  });

  const contentDetails = contentDetailsData?.data;

  const currentLayout = useMemo(() => {
    return JSON.parse(contentDetails?.layout_config ?? "{}") as SplitContentNode;
  }, [contentDetails]);

  const hasChanges = useMemo(() => {
    return contentDetails?.layout_config !== JSON.stringify(updatedLayout);
  }, [contentDetails?.layout_config, updatedLayout]);

  useEffect(() => {
    setUpdatedLayout(currentLayout);
  }, [currentLayout, setUpdatedLayout]);

  const { data: screenSizeDetails, isLoading: isScreenSizeDetailsLoading } = useQuery({
    queryFn: () => getScreenSizeDetails(String(contentDetails?.screenSizeId), String(activeFirm?.firmGuid)),
    queryKey: ["getScreenSizeDetails", contentDetails?.screenSizeId],
    enabled: Boolean(contentDetails?.screenSizeId),
  });
  const screenSizeDetailsData = screenSizeDetails?.data;

  const { containerRef, width: containerWidth } = useContainerWidth(isLoading || isScreenSizeDetailsLoading);

  if (
    !contentDetails?.screenSizeId ||
    !contentDetails?.layout_config ||
    !screenSizeDetailsData ||
    isScreenSizeDetailsLoading
  ) {
    return null;
  }

  return (
    <>
      <PageTitle
        title={contentDetails.name}
        rightContent={
          <div className="flex items-center gap-2">
            <Button
              disabled={!hasChanges || updateSplitContentMutation.isPending}
              onClick={() => updateSplitContentMutation.mutate()}
              size="sm"
            >
              {updateSplitContentMutation.isPending ? <LoadingSmall /> : <SaveIcon />} {t("common.save")}
            </Button>
          </div>
        }
      />
      <div ref={containerRef} className="relative">
        <SplitContentEditor
          viewportSize={`${screenSizeDetailsData?.width}x${screenSizeDetailsData?.height}`}
          layoutConfig={currentLayout}
          containerWidth={containerWidth}
          onLayoutChange={(layout) => {
            setUpdatedLayout(layout);
          }}
          defaultScreenSizeGuid={contentDetails?.screenSizeId}
        />
      </div>
    </>
  );
};
